import { useEffect } from "react";
import { Mic, Square } from "lucide-react";
import { Button } from "@/components/forms/Button";
import { useDictation } from "@/hooks/useDictation";
import { cn } from "@/lib/utils";

/**
 * Le bouton qui écoute, et ce qu'il donne à voir pendant qu'il écoute.
 *
 * Trois choses à montrer, et pas une de plus : que ça écoute (le bouton change
 * et une pastille bat), ce qui est compris à l'instant (le texte provisoire),
 * et pourquoi ça s'est arrêté quand ça s'arrête. Le texte reconnu, lui, part
 * dans le champ : c'est là qu'il se relit et se corrige.
 *
 * Sans reconnaissance vocale — Firefox, un navigateur ancien — le bouton
 * n'apparaît pas et une phrase dit quoi faire à la place. Un bouton qui ne
 * marcherait pas vaut moins que pas de bouton.
 */
export function Dictee({
  onTexte,
  autoStart = false,
  className,
}: {
  onTexte: (phrase: string) => void;
  /** Démarre dès l'affichage : le geste qui a ouvert le bloc voulait dicter. */
  autoStart?: boolean;
  className?: string;
}) {
  const { disponible, ecoute, provisoire, erreur, demarrer, basculer } = useDictation({ onTexte });

  // Pas de garde « une seule fois » : en développement, React monte, démonte
  // et remonte — la garde laissait alors une écoute annulée au démontage et
  // aucune relance. `demarrer` est sans effet s'il écoute déjà.
  useEffect(() => {
    if (!autoStart || !disponible) return;
    demarrer();
  }, [autoStart, disponible, demarrer]);

  if (!disponible) {
    return (
      <p className={cn("text-[11px] text-text-muted", className)}>
        La dictée n’est pas disponible dans ce navigateur. Sur un téléphone, le
        micro du clavier fait la même chose.
      </p>
    );
  }

  return (
    <div className={cn("space-y-2", className)}>
      <Button
        type="button"
        variant={ecoute ? "secondary" : "primary"}
        onClick={basculer}
        aria-pressed={ecoute}
      >
        {ecoute ? (
          <>
            <Square size={16} />
            {/* La pastille bat trois fois puis s'immobilise, comme partout
                ailleurs : un point qui clignote sans fin cesse d'être vu. */}
            <span className="w-2 h-2 rounded-full bg-danger pulse-danger" aria-hidden />
            J’écoute — toucher pour arrêter
          </>
        ) : (
          <>
            <Mic size={16} />
            Dicter
          </>
        )}
      </Button>

      {/* Lu à voix haute par un lecteur d'écran au fil de la dictée : c'est le
          seul retour qu'il a que le micro entend quelque chose. */}
      <p aria-live="polite" className="text-[12.5px] text-text-muted min-h-[1.2em]">
        {ecoute && (provisoire || "Parlez : décrivez le chantier, les pièces, les travaux.")}
      </p>

      {erreur && <p className="text-[12.5px] text-danger-ink">{erreur}</p>}
    </div>
  );
}
